import {
  ErrorDisplayInfo,
  ErrorInfo,
  ErrorList,
  ErrorListContext
} from '@tableau/data-catalog-shared';
import * as React from 'react';

import { defaultMemoize } from 'reselect';

export type ErrorListState = {
  readonly errors: ReadonlyArray<ErrorInfo>;
};

/**
 * Provides an ErrorList React Context for tracking a list of errors.
 * The list should be scoped to some reasonable context, such as a place page.
 * Children that emit errors can use ErrorReporter to do so, or add errors directly via addError.
 * Those errors can be handled by children that subscribe, such as ErrorToasts.
 */
export class ErrorListProvider extends React.Component<unknown, ErrorListState> {

  // tslint:disable-next-line:typedef
  private memoizedCreateErrorList = defaultMemoize(createErrorList);

  public constructor(props: unknown) {
    super(props);
    this.state = { errors: [] };
  }

  private nextErrorId = 1;

  public render() {
    const errorList = this.memoizedCreateErrorList(this.state.errors, this);
    return (
      <ErrorListContext.Provider value={errorList}>
        {this.props.children}
      </ErrorListContext.Provider>
    );
  }

  public addError = (errorDisplayInfo: ErrorDisplayInfo): string => {
    const errorId = `error_${this.nextErrorId++}`;
    const newError: ErrorInfo = {
      ...errorDisplayInfo,
      errorId,
    };

    this.setState(prevState => ({
      errors: prevState.errors.concat(newError)
    }));

    return errorId;
  }

  public removeError = (errorId: string) => {
    this.setState(prevState => ({
      errors: prevState.errors.filter(e => e.errorId !== errorId)
    }));
  }

  public clearErrors = () => {
    this.setState({ errors: [] });
  }
}

function createErrorList(
  errors: ReadonlyArray<ErrorInfo>,
  provider: Omit<ErrorList, 'errors'>
): ErrorList {
  return {
    errors,
    addError: provider.addError,
    removeError: provider.removeError,
    clearErrors: provider.clearErrors,
  };
}
